import React from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';

import { PageHeader, DataTableCard, FilterChips, EntityDrawer } from '../ui';
import GACompanyForm from './GACompanyForm';
import buildGACompanyColumns from './buildGACompanyColumns';
import useGACompaniesPage from './useGACompaniesPage';

const GACompanies = () => {
  const p = useGACompaniesPage();

  const columns = buildGACompanyColumns({ t: p.t });

  const filterOptions = [
    { value: 'all', label: p.t('All') },
    { value: 'active', label: p.t('ga.status.active') },
    { value: 'suspended', label: p.t('ga.status.suspended') },
    { value: 'archived', label: p.t('ga.status.archived') },
  ];

  return (
    <Box>
      <PageHeader
        title={p.t('ga.companies.title')}
        subtitle={p.t('{{count}} workspaces on the platform', { count: p.total, defaultValue: `${p.total} workspaces on the platform` })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('ga.companies.search') }}
        actions={[
          { label: p.t('Create Company'), icon: AddIcon, variant: 'contained', onClick: p.openCreateDrawer },
        ]}
      />

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        toolbar={(
          <FilterChips options={filterOptions} value={p.statusFilter} onChange={p.setStatusFilter} filtersLabel={p.t('Filters')} />
        )}
        empty={p.isNoMatch ? {
          kind: 'no-match',
          title: p.t('No companies match "{{query}}"', { query: p.search, defaultValue: `No companies match "${p.search}"` }),
          description: p.t('Try a different search or clear the filter.', { defaultValue: 'Try a different search or clear the filter.' }),
          actions: [{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); p.setStatusFilter('all'); } }],
        } : {
          kind: 'none',
          icon: BusinessIcon,
          title: p.t('No companies yet', { defaultValue: 'No companies yet' }),
          description: p.t('Create a company to onboard a new workspace onto the platform.', { defaultValue: 'Create a company to onboard a new workspace onto the platform.' }),
          actions: [{ label: p.t('Create Company'), variant: 'contained', onClick: p.openCreateDrawer }],
        }}
        error={{
          title: p.t("Couldn't load companies", { defaultValue: "Couldn't load companies" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: () => p.fetchCompanies(true),
        }}
        permission={{
          title: p.t('ga.error.forbidden'),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        onRowClick={(params) => p.goToDetail(params.row)}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <EntityDrawer
        open={p.drawerOpen}
        onClose={p.closeDrawer}
        title={p.t('ga.companies.create')}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        cancelLabel={p.t('ga.common.cancel')}
        primaryLabel={p.saving ? p.t('Creating...') : p.t('ga.common.save')}
        onPrimary={p.handleSave}
        primaryDisabled={p.saving || !p.isFormValid}
        loading={p.saving}
      >
        <GACompanyForm form={p.form} setForm={p.setForm} errors={p.formErrors} />
      </EntityDrawer>

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GACompanies;
